// src/components/admin/MatchManagement.js
import { Add as AddIcon, Search as SearchIcon } from "@mui/icons-material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ViewIcon from "@mui/icons-material/RemoveRedEye";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  FormControl,
} from "@mui/material";
import moment from "moment";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  addMatch,
  deleteMatch,
  getMatches,
  updateMatch,
} from "../../../../store/actions/matchesActions";

const MatchManagement = ({ provider }) => {
  const dispatch = useDispatch();
  const matchesState = useSelector((state) => state.matches || {});
  const matchesFromStore = matchesState.list || [];
  const seasonsState = useSelector((state) => state.seasons || {});
  const seasonsFromStore = seasonsState.list || [];

  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingMatch, setEditingMatch] = useState(null);
  const [openView, setOpenView] = useState(false);

  const [matchForm, setMatchForm] = useState({
    provider: provider?._id || "",
    season: null,
    title: "",
    description: "",
    teamHome: "",
    teamAway: "",
    matchDate: new Date(),
    venue: "",
    isSeasonMatch: false,
    status: "upcoming",
  });

  const matchesPerPage = 8;

  // ✅ Load matches and seasons
  useEffect(() => {
    if (provider?._id) {
      setLoading(true);
      dispatch(getMatches(provider._id));
      dispatch({ type: "GET_SEASONS_IF_NOT" });
    }
  }, [provider, dispatch]);

  useEffect(() => setLoading(false), [matchesFromStore]);
  useEffect(() => setPage(1), [search]);

  // ✅ Search & pagination
  const filtered = matchesFromStore.filter((m) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      (m.title || "").toLowerCase().includes(q) ||
      (m.teamHome || "").toLowerCase().includes(q) ||
      (m.teamAway || "").toLowerCase().includes(q)
    );
  });

  const totalPages = Math.ceil(filtered.length / matchesPerPage) || 1;
  const current = filtered.slice(
    (page - 1) * matchesPerPage,
    page * matchesPerPage
  );

  // ✅ Dialog Handlers
  const openAddDialog = () => {
    setEditingMatch(null);
    setMatchForm({
      provider: provider?._id || "",
      season: null,
      title: "",
      description: "",
      teamHome: "",
      teamAway: "",
      matchDate: new Date(),
      venue: "",
      isSeasonMatch: false,
      status: "upcoming",
    });
    setOpenDialog(true);
  };

  const openEditDialog = (m) => {
    setEditingMatch(m);
    setMatchForm({
      provider: m.provider?._id || m.provider,
      season: m.season?._id || m.season || null,
      title: m.title,
      description: m.description,
      teamHome: m.teamHome,
      teamAway: m.teamAway,
      matchDate: m.matchDate ? new Date(m.matchDate) : new Date(),
      venue: m.venue,
      isSeasonMatch: !!m.season,
      status: m.status || "upcoming",
    });
    setOpenDialog(true);
  };

  const handleSave = async () => {
    const payload = { ...matchForm, provider: provider._id };
    if (payload.season) payload.isSeasonMatch = true;

    if (editingMatch?._id) {
      await dispatch(updateMatch({ id: editingMatch._id, payload }));
    } else {
      await dispatch(addMatch(payload));
    }

    dispatch(getMatches(provider._id));
    setOpenDialog(false);
    setEditingMatch(null);
  };

  const handleDelete = (m) => {
    if (!window.confirm(`Delete this match? ${m.title}`)) return;
    dispatch(deleteMatch({ matchId: m._id, providerId: provider._id }));
  };

  return (
    <Box sx={{ maxWidth: "99%", mx: "auto" }}>
      <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
        <TextField
          size="small"
          placeholder="Search matches..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1 }} />,
          }}
          sx={{ flex: 1 }}
        />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openAddDialog}
        >
          Add Match
        </Button>
      </Box>

      {/* ✅ Matches Table */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : current.length === 0 ? (
        <Typography>No matches found</Typography>
      ) : (
        <Stack spacing={2}>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Match ID</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>Teams</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Venue</TableCell>
                  <TableCell>Season</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {current.map((m) => (
                  <TableRow key={m._id}>
                    <TableCell>{m.matchId || "-"}</TableCell>
                    <TableCell>{m.title}</TableCell>
                    <TableCell>
                      {m.teamHome} vs {m.teamAway}
                    </TableCell>
                    <TableCell>
                      {m.matchDate
                        ? moment(m.matchDate).format("DD-MM-YYYY HH:mm")
                        : "-"}
                    </TableCell>
                    <TableCell>{m.venue}</TableCell>
                    <TableCell>
                      {m.season ? m.season.name || m.season : "Independent"}
                    </TableCell>
                    <TableCell>{m.status}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        onClick={() => setOpenView(m)}
                        color="info"
                      >
                        <ViewIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => openEditDialog(m)}
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => handleDelete(m)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(e, v) => setPage(v)}
          />
        </Box>
      )}

      {/* Add/Edit Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{editingMatch ? "Edit Match" : "Add Match"}</DialogTitle>
        <DialogContent
          sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}
        >
          <FormControl fullWidth>
            <InputLabel id="season-select-label">Season (optional)</InputLabel>
            <Select
              labelId="season-select-label"
              value={matchForm.season || ""}
              label="Season (optional)"
              onChange={(e) =>
                setMatchForm({
                  ...matchForm,
                  season: e.target.value || null,
                  isSeasonMatch: !!e.target.value,
                })
              }
            >
              <MenuItem value="">Independent Match</MenuItem>
              {seasonsFromStore.map((s) => (
                <MenuItem key={s._id} value={s._id}>
                  {s.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Title"
            value={matchForm.title}
            onChange={(e) =>
              setMatchForm({ ...matchForm, title: e.target.value })
            }
          />

          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              label="Team Home"
              value={matchForm.teamHome}
              onChange={(e) =>
                setMatchForm({ ...matchForm, teamHome: e.target.value })
              }
            />
            <TextField
              label="Team Away"
              value={matchForm.teamAway}
              onChange={(e) =>
                setMatchForm({ ...matchForm, teamAway: e.target.value })
              }
            />
            <TextField
              label="Venue"
              value={matchForm.venue}
              onChange={(e) =>
                setMatchForm({ ...matchForm, venue: e.target.value })
              }
            />
          </Box>

          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              label="Match Date"
              type="datetime-local"
              value={moment(matchForm.matchDate).format("YYYY-MM-DDTHH:mm")}
              onChange={(e) =>
                setMatchForm({ ...matchForm, matchDate: e.target.value })
              }
              InputLabelProps={{ shrink: true }}
            />
            <FormControl fullWidth>
              <InputLabel id="match-status-label">Status</InputLabel>
              <Select
                labelId="match-status-label"
                value={matchForm.status}
                label="Status"
                onChange={(e) =>
                  setMatchForm({ ...matchForm, status: e.target.value })
                }
              >
                <MenuItem value="upcoming">Upcoming</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Match Details */}
      <Dialog
        open={Boolean(openView)}
        onClose={() => setOpenView(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Match Details</DialogTitle>
        <DialogContent>
          {openView && (
            <Box>
              <Typography variant="h6">{openView.title}</Typography>
              <Typography>
                {openView.teamHome} vs {openView.teamAway}
              </Typography>
              <Typography>
                Date: {moment(openView.matchDate).format("DD-MM-YYYY HH:mm")}
              </Typography>
              <Typography>Venue: {openView.venue}</Typography>
              <Typography>Match ID: {openView.matchId}</Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default MatchManagement;
